/**
 * ステップ見積もり -超松竹梅- （実験的機能）。
 *
 * 項目の選択肢を表形式で登録すると、`_a` `_b` サフィックスのグループが
 * ステップナビゲーションの選択肢になり、「見積もりを試す >」でウィザードが
 * 起動する。最後まで進むと Vivliostyle 基盤（BE 経由）で御見積書 PDF を
 * 生成できる。
 *
 * 表はツールプリセット（tool_id=step-estimate）として保存できる（本人のみ。
 * 他ツールと違い相互フォローへの共有は行わない）。
 * 保存は明示操作（プリセットストリップ）で、編集中はローカル state のみ。
 *
 * 初期状態は空の 1 行（未保存）。
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import BottomAppBarButton from "../../../components/bottom-app-bar/BottomAppBarButton";
import Input from "../../../components/form-item/Input";
import ToolTip from "../../../components/tooltip/ToolTip";
import logoImage from "../../../assets/images/card_tool_step_estimate.webp";
import ItemTableEditor from "./components/ItemTableEditor";
import EstimateWizard from "./components/EstimateWizard";
import PresetStrip from "./components/PresetStrip";
import { buildSteps, type EstimateItemRow } from "./utils/estimateModel";
import {
  STEP_ESTIMATE_SCHEMA_VERSION,
  STEP_ESTIMATE_TOOL_ID,
  buildPresetPayload,
  createEmptyIssuer,
  createEmptyRow,
  sanitizePresetPayload,
  type EstimateIssuer,
} from "./utils/presetPayload";
import { issuerFromProfile, withProfileFallback } from "./utils/issuer";
// @ts-ignore - JS モジュール（型定義なし）。複数行に折ると ts-ignore が効かないため 1 行で書く。
import { useGetCurrentUserQuery } from "../../../app/accounts/authuser";

/** ？アイコンのホバーで表示するツールの説明 */
const HELP_DESCRIPTION = [
  "項目の選択肢を表に登録して、ステップナビゲーションで見積もりを作るツール（実験的機能）。",
  "項目名の末尾に _a _b … を付けると同じ名前の項目が 1 つのステップの選択肢になります。",
  "2 択で一方の単価が 0 のステップは、ウィザード上でトグル（含める / 含めない）になります。",
  "単価には数値のほか、他の項目名（その項目の単価を参照）や「一式」などの文字列（計算外）も書けます。",
  "単位を % にすると数量がパーセント値になり、金額は単価の百分率になります（単価参照と組み合わせると歩合項目を作れます）。",
  "数量可変を ON にすると、ウィザードで範囲内の数量を選べます。",
  "選択肢が無く数量も固定の項目はステップに出さず、最終確認の明細にのみ含めます。",
  "項目説明・項目サムネイルはステップ全体の表示、オプションテキスト・オプションサムネイルは各選択肢のカードに表示されます。",
  "表はプリセットとして保存されます（保存した本人のみが見られます）。",
  "最後まで進むと御見積書 PDF を生成します。",
].join("\n");

const HeaderLogo = styled.img`
  width: min(420px, 100%);
  object-fit: contain;
  align-self: flex-start;
`;

const IssuerFieldset = styled.fieldset.attrs({ className: "u-col u-gap16" })`
  margin: 0;
  padding: 16px;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;

  legend {
    padding: 0 8px;
  }
`;

const StepEstimate: React.FC = () => {
  const [rows, setRows] = useState<EstimateItemRow[]>(() => [createEmptyRow()]);
  // 宛名・件名・発行者は表と一緒にプリセットへ保存する（ウィザードの最後ではなく
  // 表編集の時点で入力しておける）。
  const [subject, setSubject] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [issuer, setIssuer] = useState<EstimateIssuer>(createEmptyIssuer);
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  const steps = useMemo(() => buildSteps(rows), [rows]);

  // 発行者の未入力項目はマイページの登録内容（所属・タイトル・請求先住所）で
  // 補う。PDF 生成時に BE も同じ規則で補うので、画面に見えている内容が
  // そのまま刷られる（規則は utils/issuer.ts を参照）。
  const { data: userData } = useGetCurrentUserQuery();
  const profileIssuer = useMemo(
    () => issuerFromProfile(userData?.data),
    [userData],
  );
  // プリセット読み込み時（コールバック内）からも最新の登録内容を見たいので控える
  const profileIssuerRef = useRef(profileIssuer);
  profileIssuerRef.current = profileIssuer;

  useEffect(() => {
    setIssuer((prev) => withProfileFallback(prev, profileIssuer));
  }, [profileIssuer]);

  const patchIssuer = useCallback(
    (patch: Partial<EstimateIssuer>) =>
      setIssuer((prev) => ({ ...prev, ...patch })),
    [],
  );

  const getPayload = useCallback(
    () => buildPresetPayload(rows, subject, customerName, issuer),
    [rows, subject, customerName, issuer],
  );

  const applyPayload = useCallback((payload: unknown) => {
    const sanitized = sanitizePresetPayload(payload);
    if (!sanitized) return null;
    setRows(sanitized.rows);
    setSubject(sanitized.subject);
    setCustomerName(sanitized.customerName);
    // 発行者が保存されていないプリセット（v2 以前や未入力のまま保存したもの）は
    // 空欄のままにせず登録内容で補う。
    const nextIssuer = withProfileFallback(
      sanitized.issuer,
      profileIssuerRef.current,
    );
    setIssuer(nextIssuer);
    return buildPresetPayload(
      sanitized.rows,
      sanitized.subject,
      sanitized.customerName,
      nextIssuer,
    );
  }, []);

  return (
    // 下端は BottomAppBar（高さ 80px）に隠れないよう余白を確保する
    <div className="u-col-center u-gap24" style={{ padding: "32px 0 120px" }}>
      <div className="u-wrapper u-col u-gap8">
        <HeaderLogo src={logoImage} alt="ステップ見積もり -超松竹梅-" />
        <div className="u-row u-gap8 u-align-center">
          <h1 className="heading-24">ステップ見積もり -超松竹梅-</h1>
          <ToolTip description={HELP_DESCRIPTION}>
            <span
              className="material-symbols-rounded"
              style={{ color: "var(--soremo-light-gray)", fontSize: 20 }}
            >
              help
            </span>
          </ToolTip>
        </div>
        <p className="bodytext-13" style={{ color: "var(--soremo-gray)" }}>
          項目の選択肢を表に登録するだけで、ステップナビゲーションによる見積もり作成と御見積書
          PDF の書き出しができます。
        </p>
      </div>

      <div className="u-wrapper u-col u-gap16">
        <PresetStrip
          toolId={STEP_ESTIMATE_TOOL_ID}
          schemaVersion={STEP_ESTIMATE_SCHEMA_VERSION}
          defaultName="無題の見積もり表"
          getPayload={getPayload}
          applyPayload={applyPayload}
        />

        {/* 宛名・件名は表と一緒にプリセットへ保存され、御見積書 PDF に載る */}
        <div className="u-row u-gap16" style={{ flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 240px" }}>
            <Input
              label="宛名（「御中」は自動で付きます）"
              option="optional"
              placeholder="例: 株式会社サンプル"
              maxlength={100}
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
          </div>
          <div style={{ flex: "1 1 240px" }}>
            <Input
              label="件名"
              option="optional"
              placeholder="例: 企業VP 音楽制作費 御見積"
              maxlength={100}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
        </div>

        {/* 発行者欄。マイページの登録内容が初期値だが、案件ごとに調整できる */}
        <IssuerFieldset>
          <legend className="bodytext-13" style={{ color: "var(--soremo-gray)" }}>
            発行者（御見積書の右上に入ります）
          </legend>
          <div className="u-row u-gap16" style={{ flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 240px" }}>
              <Input
                label="会社名"
                option="optional"
                placeholder="例: 株式会社ソレモ"
                maxlength={100}
                value={issuer.companyName}
                onChange={(e) => patchIssuer({ companyName: e.target.value })}
              />
            </div>
            <div style={{ flex: "1 1 240px" }}>
              <Input
                label="肩書き・氏名"
                option="optional"
                placeholder="例: プロデューサー 山田太郎"
                maxlength={100}
                value={issuer.jobTitle}
                onChange={(e) => patchIssuer({ jobTitle: e.target.value })}
              />
            </div>
          </div>
          <div className="u-row u-gap16" style={{ flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 240px" }}>
              <Input
                label="住所 1 行目"
                option="optional"
                placeholder="例: 〒135-0064 東京都江東区青海2-7-4"
                maxlength={100}
                value={issuer.addressLine1}
                onChange={(e) => patchIssuer({ addressLine1: e.target.value })}
              />
            </div>
            <div style={{ flex: "1 1 240px" }}>
              <Input
                label="住所 2 行目"
                option="optional"
                placeholder="例: theSOHO1226"
                maxlength={100}
                value={issuer.addressLine2}
                onChange={(e) => patchIssuer({ addressLine2: e.target.value })}
              />
            </div>
          </div>
          <span className="bodytext-11" style={{ color: "var(--soremo-gray)" }}>
            ロゴ・社印・サインはマイページの「御見積書の印影」で設定します。
          </span>
        </IssuerFieldset>

        <ItemTableEditor rows={rows} onChange={setRows} />
      </div>

      {/* 画面下の共通操作バー。ウィザード表示中はウィザード側のバーに切り替わる */}
      {!isWizardOpen && (
        <BottomAppBarButton
          before={null}
          after="見積もりを試す"
          afterIcon="chevron_right"
          afterDisabled={steps.length === 0}
          onClickAfter={() => setIsWizardOpen(true)}
        />
      )}

      {isWizardOpen && (
        <EstimateWizard
          rows={rows}
          subject={subject}
          customerName={customerName}
          issuer={issuer}
          onClose={() => setIsWizardOpen(false)}
        />
      )}
    </div>
  );
};

export default StepEstimate;
